import { Link } from 'react-router-dom';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import { PageHero } from '@/components/PageHero';
import { NotifyForm } from '@/components/NotifyForm';
import { Reveal } from '@/components/Reveal';
import { useSeo } from '@/seo/useSeo';

type Piece = { icon: SvgIconComponent; title: string; body: string };

const pieces: Piece[] = [
  {
    icon: DashboardOutlinedIcon,
    title: 'One rollup, not four exports',
    body: 'Ticket volume, SLA performance, device health, security posture, and compliance gaps roll up into one executive view — no manual export-and-reconcile step across separate tools the night before.',
  },
  {
    icon: FactCheckOutlinedIcon,
    title: 'Compliance posture that\'s already there',
    body: 'The same control tracking behind the compliance module (implemented, partial, planned, per framework) feeds the review directly — you\'re presenting real posture, not reconstructing it from memory.',
  },
  {
    icon: InsightsOutlinedIcon,
    title: 'A trend is an actual trend',
    body: 'Because the underlying records are the same system over time, a quarter-over-quarter comparison is the same data measured twice — not four separate point-in-time snapshots stitched together and hoped to be comparable.',
  },
  {
    icon: CalendarMonthOutlinedIcon,
    title: 'Cheap enough to actually happen every cycle',
    body: 'The programs that survive are the ones that don\'t require a night of manual slide-building before every single review — automation is what keeps the cadence real.',
  },
];

export function SolutionsVcio(): JSX.Element {
  useSeo({
    title: 'For vCIOs',
    description:
      'Executive dashboards and QBR generation built from the same live ticket, device, security, and compliance data every other Nexus module already has — not rebuilt by hand every quarter.',
    path: '/solutions/vcio',
  });

  return (
    <div>
      <PageHero
        eyebrow="For vCIOs"
        title="The review that builds itself from data that already exists."
        subtitle="A good vCIO program is one of the highest-value things an MSP offers — and one of the easiest to let slip when building the deck means pulling numbers from four tools by hand."
      >
        <Reveal delay={150}>
          <div className="mt-9 flex justify-center">
            <NotifyForm />
          </div>
        </Reveal>
      </PageHero>

      <section className="py-16 md:py-24">
        <div className="container-content">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {pieces.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={p.title} delay={i * 90}>
                  <div className="h-full rounded-2xl border border-white/10 bg-white/[0.02] p-6">
                    <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light">
                      <Icon fontSize="small" />
                    </div>
                    <h3 className="font-display text-[17px] font-semibold text-nexus-ink">{p.title}</h3>
                    <p className="mt-1.5 text-sm text-white/65">{p.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content max-w-[760px]">
          <Reveal>
            <h2 className="font-display text-2xl font-bold text-nexus-ink md:text-3xl">Go deeper</h2>
            <p className="mt-4 text-[15px] leading-relaxed text-white/75">
              Read the full case for automated vCIO reporting on the{' '}
              <Link
                to="/blog/executive-dashboards-and-vcio-reporting-that-build-themselves"
                className="text-nexus-purple-light no-underline hover:underline"
              >
                blog
              </Link>
              , or see exactly what the{' '}
              <Link to="/platform/compliance-qbr" className="text-nexus-purple-light no-underline hover:underline">
                compliance &amp; QBR module
              </Link>{' '}
              tracks today — this layer is newer than the ticketing and monitoring cores it reads
              from, and we say that plainly rather than let a polished dashboard screenshot imply
              otherwise.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <Reveal>
            <div className="rounded-[28px] border border-nexus-purple/35 bg-gradient-to-br from-[#2a1450] to-[#120a26] p-8 text-center md:p-16">
              <h2 className="font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
                See a QBR built from real data, not a template.
              </h2>
              <p className="mx-auto mt-3 max-w-[520px] text-white/70">
                Join the design-partner cohort for a walkthrough of how the rollup actually works.
              </p>
              <div className="mt-8 flex justify-center">
                <NotifyForm />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
